import { io, Socket } from 'socket.io-client';
import type { MeasurementShape } from '@/types';

const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3001';

export interface SessionState {
  session: {
    id: string;
    startedAt: string;
  };
  participants: Array<{
    userId: string;
    displayName: string;
    role: string;
  }>;
  maps: Array<{
    id: string;
    sessionId: string;
    name: string;
    width: number;
    height: number;
    gridSize: number;
    gridColor: string;
    gridOpacity: number;
    showGrid: boolean;
    gridOffsetX: number;
    gridOffsetY: number;
    backgroundOffsetX: number;
    backgroundOffsetY: number;
    backgroundScaleX: number;
    backgroundScaleY: number;
    backgroundUrl: string | null;
    isActive: boolean;
    /** @deprecated stale on the wire; read losEnabled. */
    visionMode: string;
    losEnabled: boolean;
    showBarriers: boolean;
    renderMode: string;
    lightingEnabled: boolean;
  }>;
  tokens: Array<{
    id: string;
    mapId: string;
    name: string;
    ownerUserId: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
    imageUrl: string | null;
    color: string | null;
    visibility: string;
    visionRadius: number;
    characterId: string | null;
    lightRadius: number | null;
    torchActive: boolean;
    lowLightVision: boolean;
    darkvision: boolean;
  }>;
  lightSources: Array<{
    id: string;
    mapId: string;
    x: number;
    y: number;
    radius: number;
    brightRadius: number;
    dimRadius: number;
    color: string;
    falloff: number;
  }>;
  fog: Array<{
    mapId: string;
    revealedData: any;
  }>;
  barriers: Array<{
    id: string;
    mapId: string;
    geometry: {
      points: Array<{ x: number; y: number }>;
      closed?: boolean;
    };
    blocksVision?: boolean;
    blocksMovement?: boolean;
  }>;
  /**
   * Initiative for the active map. Always present — defaults to
   * `{ mapId, entries: [], currentIndex: 0, round: 1 }` when no row exists
   * (per spec iter-4). Read this unconditionally.
   */
  initiative?: {
    mapId: string;
    entries: Array<{ tokenId: string; name: string; value: number }>;
    currentIndex: number;
    round: number;
  };
}

export interface TokenMovedEvent {
  tokenId: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  userId: string;
  timestamp: string;
  waypoints?: Array<{ x: number; y: number }>; // Optional waypoints for animated path
  instant?: boolean; // Phase 3c: If true, move instantly without animation (keyboard moves)
}

export interface TokenCreatedEvent {
  token: {
    id: string;
    mapId: string;
    name: string;
    ownerUserId?: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
    imageUrl?: string | null;
    color?: string | null;
    visibility?: string;
    visionRadius?: number;
    characterId?: string | null;
  };
  userId: string;
}

export interface TokenDeletedEvent {
  tokenId: string;
  userId: string;
}

/**
 * Broadcast for bulk delete. `tokenIds` lists everything that was
 * actually removed server-side (which may be a subset of what the
 * caller requested if some IDs were rejected by the permission
 * check). Clients should drop every listed ID from their store.
 */
export interface TokensDeletedEvent {
  tokenIds: string[];
  userId: string;
}

export interface TokenUpdatedEvent {
  tokenId: string;
  updates: {
    visibility?: 'visible' | 'gm_only';
    color?: string;
    name?: string;
    visionRadius?: number;
    /** Repoint the token to a different character (GM-only). */
    characterId?: string | null;
    /** GM-only: whether players may see this token's HP bar. */
    hpBarVisibleToPlayers?: boolean;
    /** Status/condition keys — must sync to every client (issue #139). */
    statuses?: string[];
    /** Owner-or-GM token transforms — also broadcast but were never applied. */
    rotation?: number;
    width?: number;
    height?: number;
    /** Light radius in grid squares, or null to clear. GM any token; player own tokens. */
    lightRadius?: number | null;
    /** Whether the token's light is currently active. GM or owner. */
    torchActive?: boolean;
    /** GM-only: token sees dim light as bright (low-light vision). */
    lowLightVision?: boolean;
    /** GM-only: token sees in darkness (darkvision). */
    darkvision?: boolean;
    /** Mob token per-instance combat state (HP/statuses), or null to clear
     *  (makes the token linked again). Broadcast so every client re-renders. */
    instanceStats?: { hp: { current: number; max: number; temp: number }; statuses: string[] } | null;
  };
  userId: string;
}

export interface ParticipantJoinedEvent {
  userId: string;
  displayName: string;
  role: string;
}

export interface ParticipantLeftEvent {
  userId: string;
}

export interface ConnectionSuccessEvent {
  userId: string;
  role: string;
}

export interface ErrorEvent {
  errorType: 'permission_denied' | 'invalid_data' | 'not_found' | 'rate_limit';
  message: string;
  originalEvent?: string;
  requestId?: string;
  /**
   * Optional machine-readable reason. Initiative emits:
   *   - 'conflict'        — Serializable retry exhausted
   *   - 'no_initiative'   — advance/back fired before any set
   *   - 'empty_entries'   — set called with []
   *   - 'at_start'        — back at round=1, index=0 (no-op)
   */
  reason?: string;
}

export interface BarrierCreatedEvent {
  barrier: {
    id: string;
    mapId: string;
    points: Array<{ x: number; y: number }>;
    closed?: boolean;
    blocksVision?: boolean;
    blocksMovement?: boolean;
  };
  userId: string;
  timestamp: string;
}

export interface BarrierDeletedEvent {
  barrierId: string;
  userId: string;
  timestamp: string;
}

export interface BarrierUpdatedEvent {
  barrier: {
    id: string;
    mapId: string;
    points: Array<{ x: number; y: number }>;
    blocksVision: boolean;
    blocksMovement: boolean;
  };
  userId: string;
  timestamp: string;
}

/**
 * Server-driven GM role change. Either `oldGM` is null (orphaned-seat
 * rescue via `take_gm_control` against an offline GM) or both are set
 * (live swap via pass + accept).
 */
export interface RoleChangedEvent {
  oldGM: { userId: string; newRole: 'player' } | null;
  newGM: { userId: string; newRole: 'gm' };
  participants: Array<{ userId: string; displayName: string; role: string }>;
}

/** Sent only to the nominated target when a GM passes control. */
export interface GMHandoffOfferedEvent {
  fromUserId: string;
  fromDisplayName: string;
  targetUserId: string;
  expiresAt: number;
  sessionId: string;
}

/** Sent to both parties when the 60s offer TTL fires unaccepted. */
export interface GMHandoffExpiredEvent {
  fromUserId: string;
  targetUserId: string;
  sessionId: string;
}

export interface MapVisionUpdatedEvent {
  mapId: string;
  losEnabled?: boolean;
  showBarriers?: boolean;
  lightingEnabled?: boolean;
  renderMode?: 'dimmed' | 'darkness';
  userId: string;
  timestamp: string;
}

// ── Dynamic Lighting Events ──────────────────────────────────────────────────

export interface LightSourceCreatedEvent {
  lightSource: {
    id: string;
    mapId: string;
    x: number;
    y: number;
    radius: number;
    brightRadius: number;
    dimRadius: number;
    color: string;
    falloff: number;
  };
  userId: string;
  timestamp: string;
}

export interface LightSourceUpdatedEvent {
  lightSourceId: string;
  radius?: number;
  x?: number;
  y?: number;
  brightRadius?: number;
  dimRadius?: number;
  color?: string;
  falloff?: number;
  userId: string;
  timestamp: string;
}

export interface LightSourceDeletedEvent {
  lightSourceId: string;
  userId: string;
  timestamp: string;
}

export interface FogRevealedEvent {
  mapId: string;
  cells: Array<{ x: number; y: number }>;
  userId: string;
  timestamp: string;
}

export interface FogHiddenEvent {
  mapId: string;
  cells: Array<{ x: number; y: number }>;
  userId: string;
  timestamp: string;
}

/** Broadcast result of a bulk fog op (hide-all / clear-all). Carries the
 *  full new revealed-cell map so clients replace fog state wholesale. */
export interface FogSetAllDoneEvent {
  mapId: string;
  revealed: boolean;
  fog: Record<string, boolean>;
  userId: string;
  timestamp: string;
}

export interface ChatMessageSentEvent {
  messageId: string;
  userId: string;
  displayName: string;
  message: string;
  timestamp: string;
  isWhisper: boolean;
}

export interface DiceRolledEvent {
  rollId: string;
  userId: string;
  displayName: string;
  /** Source of the roll (e.g. "Acrobatics", "Dexterity Save", "Initiative").
   *  Optional — unlabeled rolls (raw dice tray) omit it. */
  label?: string;
  notation: string;
  total: number;
  output: string;
  rolls: Array<{
    type: string;
    value: number;
    initialValue?: number;
    modifiers?: string[];
  }>;
  timestamp: string;
  isWhisper: boolean;
}

export type ChatHistoryEntry =
  | {
      kind: 'chat_message';
      messageId: string;
      userId: string;
      displayName: string;
      message: string;
      timestamp: string;
      isWhisper: boolean;
    }
  | {
      kind: 'dice_rolled';
      rollId: string;
      userId: string;
      displayName: string;
      label?: string;
      notation: string;
      total: number;
      output: string;
      timestamp: string;
      isWhisper: boolean;
    };

export interface ChatHistoryEvent {
  entries: ChatHistoryEntry[];
}

export interface MapSwitchedEvent {
  map: {
    id: string;
    sessionId: string;
    name: string;
    width: number;
    height: number;
    gridSize: number;
    gridColor: string;
    gridOpacity: number;
    showGrid: boolean;
    gridOffsetX: number;
    gridOffsetY: number;
    backgroundOffsetX: number;
    backgroundOffsetY: number;
    backgroundScaleX: number;
    backgroundScaleY: number;
    backgroundFit: 'stretch' | 'contain' | null;
    backgroundUrl: string | null;
    isActive: boolean;
    /** @deprecated stale on the wire; read losEnabled. */
    visionMode: string;
    losEnabled: boolean;
    showBarriers: boolean;
    renderMode: string;
  };
}

export interface MapCreatedEvent {
  map: {
    id: string;
    sessionId: string;
    name: string;
    width: number;
    height: number;
    gridSize: number;
    gridColor: string;
    gridOpacity: number;
    showGrid: boolean;
    gridOffsetX: number;
    gridOffsetY: number;
    backgroundOffsetX: number;
    backgroundOffsetY: number;
    backgroundScaleX: number;
    backgroundScaleY: number;
    backgroundFit: 'stretch' | 'contain' | null;
    backgroundUrl: string | null;
    isActive: boolean;
    /** @deprecated stale on the wire; read losEnabled. */
    visionMode: string;
    losEnabled: boolean;
    showBarriers: boolean;
    renderMode: string;
  };
}

export interface MapUpdatedEvent {
  map: {
    id: string;
    sessionId: string;
    name: string;
    width: number;
    height: number;
    gridSize: number;
    gridColor: string;
    gridOpacity: number;
    showGrid: boolean;
    gridOffsetX: number;
    gridOffsetY: number;
    backgroundOffsetX: number;
    backgroundOffsetY: number;
    backgroundScaleX: number;
    backgroundScaleY: number;
    backgroundFit: 'stretch' | 'contain' | null;
    backgroundUrl: string | null;
    isActive: boolean;
    /** @deprecated stale on the wire; read losEnabled. */
    visionMode: string;
    losEnabled: boolean;
    showBarriers: boolean;
    renderMode: string;
  };
}

export interface MapDeletedEvent {
  mapId: string;
}

// ── Initiative tracker events ────────────────────────────────────────────────

export interface InitiativeEntryPayload {
  tokenId: string;
  name: string;
  value: number;
}

/**
 * `initiative_updated` — broadcast to all session participants.
 * Same shape used in `session_state.initiative` (per spec iter-4).
 */
export interface InitiativeUpdatedEvent {
  mapId: string;
  entries: InitiativeEntryPayload[];
  currentIndex: number;
  round: number;
}

/** `initiative_cleared` — broadcast to all session participants. */
export interface InitiativeClearedEvent {
  mapId: string;
}

/**
 * `ruler_updated` — transient broadcast of another participant's live ruler
 * measurement. Not persisted anywhere; clients render it ephemerally and
 * drop it when `active` goes false.
 */
export interface RulerUpdatedEvent {
  userId: string;
  displayName: string;
  mapId: string;
  start: { x: number; y: number };
  end: { x: number; y: number };
  active: boolean;
  /**
   * Measurement-template shape the drawer selected. The server defaults
   * absent/invalid values to `'line'` before re-broadcasting, so this is
   * always one of the four enum values on the receiving side. Peers
   * recompute the template geometry from start+end+shape using their own
   * active map's gridSize (identical for everyone on the same map) — the
   * geometry itself is NOT transmitted.
   */
  shape: MeasurementShape;
}

/**
 * `ping_placed` — a transient ping marker another participant (or this client,
 * echoed back) placed via left-click-and-hold. Not persisted anywhere; clients
 * render an expiring pulse and drop it after a short TTL. NOTE: distinct from
 * the `ping`/`pong` connection heartbeat — this is the in-game "look here" ping.
 */
export interface PingPlacedEvent {
  userId: string;
  mapId: string;
  x: number;
  y: number;
}

/**
 * A character's HP changed on the platform sheet (issue #135). HP is character
 * data, not a token field, so it rides its own event: the editor writes to
 * ac-mvp then emits `character_hp_changed`; the server rebroadcasts
 * `character_hp_updated` to the session and every client refetches that
 * character so the new HP shows for everyone.
 */
export interface CharacterHpUpdatedEvent {
  characterId: string;
  userId: string;
}

/**
 * A character's token classification changed (VTT #163). Classification
 * (player / friendly / enemy) is character data, not a token field, so — like
 * HP — it rides its own event: the GM writes to ac-mvp then emits
 * `character_classification_changed`; the server rebroadcasts
 * `character_classification_updated` to the session and every client refetches
 * that character, so the right-sidebar bank re-buckets and the enemy outline
 * updates live for everyone.
 */
export interface CharacterClassificationUpdatedEvent {
  characterId: string;
  userId: string;
}

/**
 * WebSocket Service
 * Manages Socket.io connection and event handling
 */
class SocketService {
  private socket: Socket | null = null;
  private connected = false;
  private currentToken: string | null = null;
  /**
   * The session room this socket has joined. Remembered so we can
   * automatically re-emit `join_session` after every reconnect —
   * socket.io reconnects create a brand-new server-side socket that
   * is no longer in the room, so without re-joining the client stops
   * receiving `io.to(sessionId)` broadcasts (chat, dice, token moves)
   * until a full page reload.
   */
  private currentSessionId: string | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly REFRESH_BUFFER_MS = 30 * 60 * 1000; // Refresh 30 minutes before expiry
  private readonly TOKEN_STORAGE_KEY = 'vtt_token';

  /**
   * Callback when authentication is permanently lost (session ended, token too old).
   * The consumer (React app) sets this to show a re-auth UI.
   */
  onAuthExpired: (() => void) | null = null;

  /**
   * Mutex to prevent concurrent refresh requests (Nakir C2).
   * Prevents race between proactive timer and connect_error handler.
   */
  private refreshInProgress = false;

  /**
   * Store token in sessionStorage and memory.
   * sessionStorage survives page refreshes but not tab/window close.
   */
  private storeToken(token: string): void {
    this.currentToken = token;
    try {
      sessionStorage.setItem(this.TOKEN_STORAGE_KEY, token);
    } catch {
      // sessionStorage unavailable (incognito, storage full) — memory-only
    }
  }

  /**
   * Retrieve token from memory or sessionStorage.
   */
  private getStoredToken(): string | null {
    if (this.currentToken) return this.currentToken;
    try {
      return sessionStorage.getItem(this.TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Clear stored token.
   */
  private clearToken(): void {
    this.currentToken = null;
    try {
      sessionStorage.removeItem(this.TOKEN_STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  /**
   * Decode JWT payload (no verification — client doesn't have the secret).
   */
  private decodeToken(token: string): { exp: number; userId: string; sessionId: string } | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch {
      return null;
    }
  }

  /**
   * Schedule proactive token refresh 30 minutes before expiry.
   */
  private scheduleTokenRefresh(token: string): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const payload = this.decodeToken(token);
    if (!payload?.exp) return;

    const now = Date.now();
    const expiresAtMs = payload.exp * 1000;

    // Token already expired — refresh immediately (Nakir W2)
    if (expiresAtMs < now) {
      console.warn('[WebSocket] Token already expired, refreshing immediately');
      this.refreshToken();
      return;
    }

    const refreshAtMs = expiresAtMs - this.REFRESH_BUFFER_MS;
    const delayMs = refreshAtMs - now;

    if (delayMs <= 0) {
      // Token within refresh window — refresh now
      this.refreshToken();
      return;
    }

    console.log(`[WebSocket] Token refresh scheduled in ${Math.round(delayMs / 60000)} minutes`);
    this.refreshTimer = setTimeout(() => this.refreshToken(), delayMs);
  }

  /**
   * Request a fresh token from the VTT server.
   * Guarded by refreshInProgress mutex to prevent races (Nakir C2).
   */
  private async refreshToken(): Promise<boolean> {
    if (this.refreshInProgress) return false;
    this.refreshInProgress = true;

    const currentToken = this.getStoredToken();
    if (!currentToken) {
      this.refreshInProgress = false;
      return false;
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || WS_URL;
      const response = await fetch(`${apiUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error('[WebSocket] Token refresh failed', error);

        if (error.requiresReauth) {
          // Session no longer valid — can't recover
          this.onAuthExpired?.();
          return false;
        }
        return false;
      }

      const data = await response.json();
      this.storeToken(data.token);

      // Update socket auth for future reconnections
      if (this.socket) {
        this.socket.auth = { token: data.token };
      }

      // Schedule next refresh
      this.scheduleTokenRefresh(data.token);

      console.log('[WebSocket] Token refreshed, expires at', data.expiresAt);
      return true;
    } catch (error) {
      console.error('[WebSocket] Token refresh error', error);
      return false;
    } finally {
      this.refreshInProgress = false; // Nakir C2: always release mutex
    }
  }

  /**
   * Check if a stored token exists (for page refresh recovery).
   * Returns the token if found. The server is the authority on whether
   * the token is too old to refresh — the client just attempts it.
   * (Nakir N11: removed duplicate client-side 7-day check)
   */
  getTokenFromStorage(): string | null {
    return this.getStoredToken();
  }

  /**
   * Connect to WebSocket server with JWT token
   */
  connect(jwtToken: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Nakir C3: Clear any existing refresh timer before setting up new connection.
      // Prevents orphaned timers from previous connect() calls (e.g., component remount).
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }

      // Store token
      this.storeToken(jwtToken);

      this.socket = io(WS_URL, {
        auth: { token: jwtToken },
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity,  // Changed from 10 — we handle re-auth ourselves
      });

      this.socket.on('connect', () => {
        console.log('[WebSocket] Connected');
        this.connected = true;
      });

      this.socket.on('connection_success', (data: ConnectionSuccessEvent) => {
        console.log('[WebSocket] Connection authenticated', data);
        // Schedule proactive refresh
        const token = this.getStoredToken();
        if (token) {
          this.scheduleTokenRefresh(token);
        }

        // Re-join the session room on reconnect. On the first connect
        // currentSessionId is still null (joinSession runs after this
        // promise resolves), so this only fires for reconnects — the
        // server's auth has just completed, so socket.sessionId is set
        // and the join will be accepted. Re-joining re-subscribes us to
        // room broadcasts and replays session_state + chat_history.
        if (this.currentSessionId) {
          console.log('[WebSocket] Reconnected — re-joining session room', this.currentSessionId);
          this.socket?.emit('join_session', { sessionId: this.currentSessionId });
        }

        resolve();
      });

      this.socket.on('connection_error', (error: any) => {
        console.error('[WebSocket] Connection error', error);
        reject(new Error(error?.data?.message || 'Connection failed'));
      });

      this.socket.on('connect_error', async (error: Error) => {
        console.error('[WebSocket] Connect error', error.message);

        // Check if it's an auth error
        if (error.message.includes('expired') || error.message.includes('Authentication failed')) {
          try {
            // Nakir C4: Pause auto-reconnect while we refresh.
            // Without this, Socket.io fires the next retry BEFORE refreshToken() completes,
            // causing the retry to use the stale token.
            if (this.socket) {
              this.socket.io.opts.reconnection = false;
            }

            const refreshed = await this.refreshToken();

            if (refreshed && this.socket) {
              // Re-enable reconnection and trigger manual reconnect with fresh token
              this.socket.io.opts.reconnection = true;
              this.socket.connect();
              console.log('[WebSocket] Token refreshed, reconnecting with new token');
            } else {
              // Refresh failed — signal auth expiry
              this.onAuthExpired?.();
            }
          } catch (err) {
            console.error('[WebSocket] Reconnect handler failed', err);
            this.onAuthExpired?.();
          } finally {
            // Always re-enable reconnection, even on error
            if (this.socket) {
              this.socket.io.opts.reconnection = true;
            }
          }
        }
      });

      this.socket.on('disconnect', (reason: string) => {
        console.log('[WebSocket] Disconnected:', reason);
        this.connected = false;
        // Socket.io handles automatic reconnection.
        // The refreshToken flow handles token expiry on connect_error.
      });

      this.socket.on('error', (error: ErrorEvent) => {
        console.error('[WebSocket] Error event', error);
      });
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
    this.currentSessionId = null;
    this.clearToken();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connected && this.socket?.connected === true;
  }

  /**
   * Join a session room
   */
  joinSession(sessionId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    // Remember the room so we can auto re-join after reconnects.
    this.currentSessionId = sessionId;
    this.socket.emit('join_session', { sessionId });
  }

  /**
   * Move a token, optionally with waypoints for animated path
   * Phase 3c: Added instant flag for keyboard moves
   */
  moveToken(tokenId: string, to: { x: number; y: number }, waypoints?: Array<{ x: number; y: number }>, instant?: boolean): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('token_move', { tokenId, to, waypoints, instant });
  }

  /**
   * Create a token (GM only — the server enforces the role gate). When
   * characterId is set the server resolves the token image from the linked
   * character sheet, so imageUrl is optional here.
   */
  createToken(payload: {
    mapId: string;
    name: string;
    x: number;
    y: number;
    characterId?: string;
    imageUrl?: string;
    color?: string;
    ownerUserId?: string | null;
    width?: number;
    height?: number;
    visibility?: 'visible' | 'gm_only';
    visionRadius?: number;
    instanceStats?: { hp: { current: number; max: number; temp: number }; statuses: string[] } | null;
  }): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('token_create', payload);
  }

  /**
   * Delete a token. Server permits GMs to delete any token and
   * lets owners delete tokens they own; other callers are rejected.
   * Server broadcasts `token_deleted` to everyone in the session on
   * success.
   */
  deleteToken(tokenId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('token_delete', { tokenId });
  }

  /**
   * Bulk-delete a set of tokens in a single emit. Wraps the
   * `tokens_delete` event so multi-select Delete avoids tripping the
   * per-event `token_delete` rate limit (5/sec) on six-plus tokens.
   * Same permission model as single-delete — server filters out IDs
   * the caller isn't allowed to delete and reports them via the
   * `error` channel without rolling back the rest. On success the
   * server broadcasts a single `tokens_deleted` event with the list
   * of deleted IDs.
   */
  deleteTokens(tokenIds: string[]): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    if (tokenIds.length === 0) return;

    this.socket.emit('tokens_delete', { tokenIds });
  }

  /**
   * Update token properties.
   * `characterId` updates are GM-only (server enforces the role gate);
   * other fields follow the existing owner-or-GM rule.
   * Server broadcasts `token_updated` to everyone in the session on success.
   */
  updateToken(
    tokenId: string,
    updates: {
      visibility?: 'visible' | 'gm_only';
      color?: string;
      name?: string;
      visionRadius?: number;
      characterId?: string | null;
      hpBarVisibleToPlayers?: boolean;
      width?: number;
      height?: number;
      rotation?: number;
      statuses?: string[];
      followsTokenId?: string | null;
      lightRadius?: number | null;
      torchActive?: boolean;
      lowLightVision?: boolean;
      darkvision?: boolean;
    }
  ): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('token_update', { tokenId, updates });
  }

  /**
   * Tell the session that a character's HP changed on the platform sheet
   * (issue #135). The write itself goes to ac-mvp over HTTP; this only signals
   * every client to refetch the character. The server rebroadcasts as
   * `character_hp_updated`.
   */
  emitCharacterHpChanged(characterId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('character_hp_changed', { characterId });
  }

  /**
   * Tell the session that a character's token classification changed (VTT #163).
   * The authoritative write goes to ac-mvp over HTTP; this only signals every
   * client to refetch the character so the bank panel re-buckets and the enemy
   * outline updates. The server rebroadcasts as `character_classification_updated`.
   */
  emitCharacterClassificationChanged(characterId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('character_classification_changed', { characterId });
  }

  /**
   * Create a barrier
   */
  createBarrier(mapId: string, points: Array<{ x: number; y: number }>, closed?: boolean): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('barrier_create', { mapId, points, closed });
  }

  /**
   * Delete a barrier
   */
  deleteBarrier(barrierId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('barrier_delete', { barrierId });
  }

  /**
   * Update a barrier's flag bits (blocksVision / blocksMovement). Server
   * persists and broadcasts `barrier_updated`; the local store applies
   * the change off the broadcast so all clients stay in sync.
   */
  updateBarrier(
    barrierId: string,
    updates: { blocksVision?: boolean; blocksMovement?: boolean }
  ): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('barrier_update', { barrierId, ...updates });
  }

  /**
   * Update map vision settings. Carries `losEnabled` (dynamic line-of-sight
   * on/off) and `showBarriers`. Both optional — the server tolerates a
   * missing field and updates only what's provided.
   */
  updateMapVision(mapId: string, losEnabled?: boolean, showBarriers?: boolean, lightingEnabled?: boolean, renderMode?: 'dimmed' | 'darkness'): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('map_vision_update', { mapId, losEnabled, showBarriers, lightingEnabled, renderMode });
  }

  /**
   * Place a standalone light source on a map (GM only).
   */
  createLightSource(
    mapId: string,
    x: number,
    y: number,
    radius?: number,
    opts?: { brightRadius?: number; dimRadius?: number; color?: string; falloff?: number }
  ): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('light_source_create', { mapId, x, y, radius, ...opts });
  }

  /**
   * Update a standalone light source's radius / bands / color / falloff (GM only).
   */
  updateLightSource(lightSourceId: string, fields: { radius?: number; x?: number; y?: number; brightRadius?: number; dimRadius?: number; color?: string; falloff?: number }): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('light_source_update', { lightSourceId, ...fields });
  }

  /**
   * Delete a standalone light source (GM only).
   */
  deleteLightSource(lightSourceId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('light_source_delete', { lightSourceId });
  }

  /**
   * Tell the server which map the GM is switching to. Server flips
   * isActive in the DB (false on every other map in the session, true
   * on this one) and broadcasts `map_switched` so every player snaps
   * to it. Without this emit, the GM's switch is local-only and the
   * session keeps showing the previously-active map to anyone else.
   */
  emitMapSwitch(mapId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('map_switch', { mapId });
  }

  /**
   * Broadcast the local ruler measurement to the session so other clients
   * can render it live. Transient only — the server relays this without
   * touching the database. `active: false` tells peers the measurement
   * ended and they should clear it.
   */
  emitRulerUpdate(payload: {
    mapId: string;
    start: { x: number; y: number };
    end: { x: number; y: number };
    active: boolean;
    shape: MeasurementShape;
  }): void {
    // Silently no-op if the socket isn't up — a dropped ruler frame is
    // harmless and we don't want a measurement to throw mid-drag.
    if (!this.socket) return;

    this.socket.emit('ruler_update', payload);
  }

  /**
   * Place an in-game ping at a world-space point. Transient relay only — the
   * server re-broadcasts as `ping_placed` to the whole session room (sender
   * included) and writes nothing to the database. Distinct from the
   * `ping`/`pong` heartbeat. No-ops if the socket isn't connected.
   */
  emitPingPlace(payload: { mapId: string; x: number; y: number }): void {
    if (!this.socket) return;

    this.socket.emit('ping_place', payload);
  }

  /**
   * Reveal fog cells
   */
  revealFog(mapId: string, cells: Array<{ x: number; y: number }>): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('fog_reveal', { mapId, cells });
  }

  /**
   * Hide fog cells
   */
  hideFog(mapId: string, cells: Array<{ x: number; y: number }>): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('fog_hide', { mapId, cells });
  }

  /**
   * Bulk-set fog for the whole map (GM only). `revealed: false` covers the
   * entire map in fog (hide all); `revealed: true` clears all fog (visible).
   */
  setAllFog(mapId: string, revealed: boolean): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('fog_set_all', { mapId, revealed });
  }

  /**
   * Claim the GM seat. Rescue path — server only allows it when the
   * current GM is not connected. Live swaps go through pass/accept.
   */
  takeGMControl(): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('take_gm_control');
  }

  /**
   * Current GM nominates a connected player as next GM. Server stores a
   * 60s offer and emits `gm_handoff_offered` to the target.
   */
  passGMControl(targetUserId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('pass_gm_control', { targetUserId });
  }

  /**
   * Target accepts a pending GM offer. `fromUserId` must match the
   * offer's originator (server cross-checks).
   */
  acceptGMControl(fromUserId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('accept_gm_control', { fromUserId });
  }

  /**
   * Target declines a pending GM offer. Server clears the offer and
   * notifies the originator.
   */
  declineGMControl(): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('decline_gm_control');
  }

  /**
   * Send a chat message
   */
  sendChatMessage(message: string, whisperTo?: string[]): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('chat_message', { message, whisperTo });
  }

  /**
   * Roll dice. Optional `label` tags the roll's source (e.g. "Acrobatics",
   * "Dexterity Save", "Initiative") so chat can render what was rolled
   * instead of a bare die. Unlabeled rolls render exactly as before.
   */
  rollDice(notation: string, label?: string, whisperTo?: string[]): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('dice_roll', { notation, label, whisperTo });
  }

  /**
   * Initiative — set the entry list (resets currentIndex/round on the server).
   */
  emitInitiativeSet(
    mapId: string,
    entries: Array<{ tokenId: string; name: string; value: number }>
  ): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('initiative_set', { mapId, entries });
  }

  /**
   * Initiative — roll a single combatant into an ALREADY-STARTED initiative.
   * Unlike the other emits this is not GM-only, but the server only updates an
   * existing row: if the GM has not started initiative the roll is rejected
   * (reason `no_initiative`) and nothing is created. Used by the character
   * sheet's clickable Init button (issue #162).
   */
  emitInitiativeRoll(
    mapId: string,
    entry: { tokenId: string; name: string; value: number }
  ): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('initiative_roll', { mapId, entry });
  }

  /** Initiative — advance the turn pointer. */
  emitInitiativeAdvance(mapId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('initiative_advance', { mapId });
  }

  /** Initiative — step the turn pointer back. No-op at round 1 / index 0. */
  emitInitiativeBack(mapId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('initiative_back', { mapId });
  }

  /** Initiative — clear the row (ends the encounter). */
  emitInitiativeClear(mapId: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('initiative_clear', { mapId });
  }

  /**
   * Send ping to server
   */
  ping(): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.emit('ping');
  }

  /**
   * Register event listener
   */
  on<T = any>(event: string, callback: (data: T) => void): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }

    this.socket.on(event, callback);
  }

  /**
   * Emit token_instance_stats_update — writes per-token HP/statuses for a mob
   * token directly on the VTT token row, bypassing the shared character sheet.
   * Server gates to GM or token owner and rebroadcasts as `token_updated`.
   */
  emitTokenInstanceStatsUpdate(
    tokenId: string,
    instanceStats: { hp: { current: number; max: number; temp: number }; statuses: string[] }
  ): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('token_instance_stats_update', { tokenId, instanceStats });
  }

  /**
   * Emit token_copy_paste — duplicate a token on the active map (Ctrl+V).
   * GM-only. Non-mob: new token shares characterId, instanceStats=null.
   * Mob: new token gets instanceStats seeded from seedHp.
   */
  emitTokenCopyPaste(payload: {
    sourceTokenId: string;
    isMob: boolean;
    seedHp?: { current: number; max: number; temp: number };
    cursorX?: number;
    cursorY?: number;
  }): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('token_copy_paste', payload);
  }

  /**
   * Remove event listener
   */
  off(event: string, callback?: (...args: any[]) => void): void {
    if (!this.socket) return;

    if (callback) {
      this.socket.off(event, callback);
    } else {
      this.socket.off(event);
    }
  }

  /**
   * Remove all listeners for an event
   */
  removeAllListeners(event?: string): void {
    if (!this.socket) return;

    this.socket.removeAllListeners(event);
  }
}

// Export singleton instance
export const socketService = new SocketService();
